import type { PostQrCodeStyle } from "@package/ui-gql/index-data-access";
import { type PropsWithChildren } from "react";
import {
  POST_QR_CODE_BACKDROP_PADDING,
  POST_QR_CODE_BACKDROP_RADIUS,
  POST_QR_CODE_BOX_PADDING,
  POST_QR_CODE_DEFAULT_TEXT,
} from "../../constants/constants";
import {
  getPostQrCodeAnchorStyle,
  resolvePostQrCodeStyle,
} from "./post-qrcode-style-utils";

type PostQrCodeBoxProps = PropsWithChildren<{
  text?: string | null;
  style?: PostQrCodeStyle | null;
  // The dragged/resized box from the visual editor fills its container,
  // letting the QR be anchored anywhere within it independently of the
  // caption; every other placement keeps the box sized to its contents
  // (min-content), which is how this has always rendered on the wall.
  fill?: boolean;
}>;

export function PostQrCodeBox({
  children,
  text,
  style,
  fill = false,
}: PostQrCodeBoxProps) {
  const resolved = resolvePostQrCodeStyle(style);

  return (
    // Translucent halo behind the card — restores the outer layer of the
    // pre-styling FlyoutQrCodePaddedContainer (`p-4 rounded-2xl bg-black/25`),
    // now with a configurable color. In `fill` mode it must exactly fill its
    // container (box-border keeps its own padding from pushing the card
    // past the container's bottom edge); otherwise it hugs the card exactly
    // like the box below it.
    <div
      className={fill ? "h-full w-full box-border" : undefined}
      style={{
        backgroundColor: resolved.backdropColor,
        borderRadius: POST_QR_CODE_BACKDROP_RADIUS,
        padding: POST_QR_CODE_BACKDROP_PADDING,
        inlineSize: fill ? "100%" : "min-content",
      }}
    >
      <div
        className={fill ? "p-8 h-full box-border relative" : "p-8"}
        style={{
          backgroundColor: resolved.boxColor,
          borderRadius: resolved.boxRadius,
          border: resolved.border,
          inlineSize: fill ? "100%" : "min-content",
        }}
      >
        {fill ? (
          <div
            style={getPostQrCodeAnchorStyle(
              resolved.qrAlignHorizontal,
              resolved.qrAlignVertical,
            )}
          >
            {children}
          </div>
        ) : (
          children
        )}
        {!resolved.textHidden &&
          (fill ? (
            <div
              className="font-bold text-center"
              style={{
                color: resolved.textColor,
                fontSize: resolved.textSize,
                // Wraps onto additional lines rather than overflowing the box
                // once it's wider than the space left by the box's own padding.
                maxWidth: `calc(100% - 2 * ${POST_QR_CODE_BOX_PADDING})`,
                ...getPostQrCodeAnchorStyle(
                  resolved.textAlignHorizontal,
                  resolved.textAlignVertical,
                ),
              }}
            >
              {text ?? POST_QR_CODE_DEFAULT_TEXT}
            </div>
          ) : (
            <div
              className="mt-8 font-bold text-center"
              style={{
                color: resolved.textColor,
                fontSize: resolved.textSize,
              }}
            >
              {text ?? POST_QR_CODE_DEFAULT_TEXT}
            </div>
          ))}
      </div>
    </div>
  );
}
